import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HiOutlinePlus } from 'react-icons/hi';
import { Button, Table } from 'flowbite-react';
import { getSchedulers, deleteScheduler } from '../../services/model/scheduler-service';
import PageContainer from "../../components/page-container";
import ConfirmDialog from "../../components/confirm-dialog/confirm-dialog";
import SchedulerSaveModal from './save';

function SchedulerListPage() {
  const { appId } = useParams();
  const [schedulers, setSchedulers] = useState([]);
  const [openCreateScheduler, setOpenCreateScheduler] = useState(false);
  const [editScheduler, setEditScheduler] = useState(null);
  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: ''
  });

  const [deletingData, setDeletingData] = useState(null);
  const [openDeleteConfirmDialog, setDeleteConfirmDialog] = useState(false);

  async function fetchSchedulers() {
    updateFormState(true, false, false);
    const data = await getSchedulers(appId);
    setSchedulers(data);
    updateFormState(false, false, false);
  }

  const handleDelete = (schedulerId) => {
    setDeletingData(schedulerId);
    setDeleteConfirmDialog(true);
  };

  const handleEdit = (scheduler) => {
    setEditScheduler(scheduler);
    setOpenCreateScheduler(true);
  };

  async function deleteSchedulerItem(status) {
    if (status) {
      updateFormState(true, false, false);
      await deleteScheduler(appId, deletingData);
      updateFormState(false, false, false);
      await fetchSchedulers();
    }
    setDeleteConfirmDialog(false);
    setDeletingData(null);
  }

  useEffect(() => {
    fetchSchedulers();
  }, [appId]);

  const handleOpenCreateScheduler = () => {
    setEditScheduler(null);
    setOpenCreateScheduler(true);
  };

  const handleOnCompletionCreateScheduler = (success) => {
    setOpenCreateScheduler(false);
    if (success) {
      fetchSchedulers();
    }
  };

  const updateFormState = (processing, success, failed, errorMessage) => {
    setFormState(Object.assign({}, { processing, success, failed, errorMessage }));
  };

  return (
    <PageContainer
      title="Schedulers"
      tooltip="Manage scheduled jobs for workflows"
      headerAction={
        <Button
          color="primary"
          className="bg-indigo-700 text-white hover:bg-indigo-800 transition-colors"
          onClick={handleOpenCreateScheduler}
        >
          <HiOutlinePlus className="mr-2" size={20} />
          New Scheduler
        </Button>
      }
    >
      {openCreateScheduler && (
        <SchedulerSaveModal
          appId={appId}
          scheduler={editScheduler}
          onCompletion={handleOnCompletionCreateScheduler}
        />
      )}

      {openDeleteConfirmDialog && (
        <ConfirmDialog
          open={openDeleteConfirmDialog}
          onCompletion={deleteSchedulerItem}
          title="Delete Confirmation"
          message="Are you sure you want to delete this scheduler? This action cannot be undone."
        />
      )}

      <div className="overflow-x-auto pt-8">
        <Table hoverable>
          <Table.Head>
            <Table.HeadCell>Name</Table.HeadCell>
            <Table.HeadCell>Target Workflow</Table.HeadCell>
            <Table.HeadCell>Schedule</Table.HeadCell>
            <Table.HeadCell>
              <span className="sr-only">Actions</span>
            </Table.HeadCell>
          </Table.Head>
          <Table.Body className="divide-y">
            {schedulers.length > 0 ? (
              schedulers.map((scheduler) => (
                <Table.Row key={scheduler.jobId} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {scheduler.name}
                  </Table.Cell>
                  <Table.Cell>{scheduler.targetId}</Table.Cell>
                  <Table.Cell>{scheduler.scheduleCron || scheduler.scheduleAt}</Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-2">
                      <Button color="light" size="xs" onClick={() => handleEdit(scheduler)}>
                        Edit
                      </Button>
                      <Button color="failure" size="xs" onClick={() => handleDelete(scheduler.jobId)}>
                        Delete
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                <Table.Cell colSpan={4} className="text-center py-4">
                  No schedulers found. Create one to get started.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    </PageContainer>
  );
}

export default SchedulerListPage;
